/**
 * Template Work Area Component
 *
 * Main content area that orchestrates the different build states.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle as CompleteIcon, Upload as UploadIcon } from '@mui/icons-material';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useTemplateBuildActions } from '../hooks/useTemplateBuildActions';
// Note: We use Redux selectors instead of useTemplateBuildStatus here
// to avoid duplicate API calls. The status hook is called in TemplateBuildPage
// which syncs data to Redux.
import { initializeTemplateBuild } from '../store/chatSlice';
import {
  selectBuildStatus,
  selectCreatedTemplateId,
  selectDocumentSummary,
  selectDocumentTitle,
  selectDraftTemplate,
  selectProgressMessage,
  selectSelectedSectionId,
  selectTaskId,
} from '../store/selectors';

import { DraftTemplateCard } from './DraftTemplateCard';
import { ProcessingScreen } from './ProcessingScreen';
import { UploadScreen } from './UploadScreen';

/**
 * Completed State - Shows success and navigation options
 */
const CompletedState: React.FC<{ templateId: string; onStartOver: () => void }> = ({
  templateId,
  onStartOver,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 500,
          p: 4,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: theme.palette.success.main + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <CompleteIcon
            sx={{ fontSize: 32, color: theme.palette.success.main }}
          />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Template Created Successfully!
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your new template is ready to use in your notes.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/templates')}
            >
              View All Templates
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(`/templates?id=${templateId}`)}
            >
              Open Template
            </Button>
          </Box>
          <Button
            variant="text"
            startIcon={<UploadIcon />}
            onClick={onStartOver}
            sx={{ mt: 1 }}
          >
            Upload Another Template
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

/**
 * Template Work Area - Main orchestrator component
 */
export const TemplateWorkArea: React.FC = () => {
  const _theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux state
  const taskId = useAppSelector(selectTaskId);
  const buildStatus = useAppSelector(selectBuildStatus);
  const draftTemplate = useAppSelector(selectDraftTemplate);
  const selectedSectionId = useAppSelector(selectSelectedSectionId);
  const createdTemplateId = useAppSelector(selectCreatedTemplateId);
  const progressMessage = useAppSelector(selectProgressMessage);
  const documentTitle = useAppSelector(selectDocumentTitle);
  const documentSummary = useAppSelector(selectDocumentSummary);

  // Derive isPolling from buildStatus (matches logic in status hooks)
  const isPolling = buildStatus === 'processing' || buildStatus === 'structure_ready';

  // Hooks
  const { selectSection } = useTemplateBuildActions(taskId, true);

  // Handle starting over - clear state and navigate to upload screen
  const handleStartOver = React.useCallback(() => {
    dispatch(initializeTemplateBuild({ taskId: null, threadId: null }));
    navigate('/templates/build');
  }, [dispatch, navigate]);

  // Determine which view to show
  const showUploadScreen = !taskId || buildStatus === 'idle';
  const showCompleted = buildStatus === 'completed' && createdTemplateId;
  
  // Check if we have a draft template with sections
  const hasDraftTemplate = draftTemplate && draftTemplate.sections.length > 0;
  
  // Check if any sections are missing example outputs (still generating previews)
  const isGeneratingPreviews = hasDraftTemplate
    ? draftTemplate.sections.some(
        section => !section.exampleOutput || section.exampleOutput.trim().length === 0
      )
    : false;

  // Upload screen - no task started yet
  if (showUploadScreen) {
    return <UploadScreen />;
  }

  // Completed screen - template saved
  if (showCompleted && createdTemplateId) {
    return <CompletedState templateId={createdTemplateId} onStartOver={handleStartOver} />;
  }

  // If we have a draft template, show it (even if status is still "processing")
  if (hasDraftTemplate) {
    return (
      <Box
        sx={{
          height: '100%',
          pt: 3,
        }}
      >
        <DraftTemplateCard
          template={draftTemplate}
          selectedSectionId={selectedSectionId}
          onSectionClick={(sectionId) => {
            // Toggle selection if clicking the same section
            selectSection(selectedSectionId === sectionId ? null : sectionId);
          }}
          isGeneratingPreviews={isGeneratingPreviews}
        />
      </Box>
    );
  }

  // Processing screen - analyzing document (no draft template yet)
  if (buildStatus === 'uploading' || buildStatus === 'processing') {
    return (
      <ProcessingScreen
        progressMessage={progressMessage}
        isPolling={isPolling}
        documentTitle={documentTitle}
        documentSummary={documentSummary}
      />
    );
  }

  // Fallback loading state
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Typography color="text.secondary">Loading...</Typography>
    </Box>
  );
};

